import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ListeningContent, ListeningSession } from '@/services/listening/listeningService';
import { IListeningContent } from '@/features/student/student-listening/interfaces';
import { ContentFilters, PaginatedResponse } from '@/services/listening/types';
import { START_PAGE } from '@/common/constants';

interface ListeningState {
    contents: IListeningContent[];
    content: ListeningContent | null;
    session: ListeningSession | null;
    loading: boolean;
    isCompletingSession: boolean;
    error: string | null;
    feedback: any | null;
    filters: ContentFilters;
    totalContents: number;
    currentPage: number;
}

const initialState: ListeningState = {
    contents: [],
    content: null,
    session: null,
    loading: false,
    isCompletingSession: false,
    error: null,
    feedback: null,
    filters: {},
    totalContents: 0,
    currentPage: START_PAGE,
};
export const fetchListeningContentsRequest = createAction<ContentFilters | undefined>(
    'listening/fetchListeningContentsRequest',
);
export const fetchListeningContentsSuccess = createAction<PaginatedResponse<IListeningContent>>(
    'listening/fetchListeningContentsSuccess',
);
export const fetchListeningContentsFailure = createAction<string>('listening/fetchListeningContentsFailure');

const listeningSlice = createSlice({
    name: 'listening',
    initialState,
    reducers: {
        setCurrentListeningContentListPage: (state, action) => {
            state.currentPage = action.payload;
        },
        setListeningContentListFilters: (state, action) => {
            state.filters = action.payload;
        },
        // Content Actions
        fetchListeningContentRequest: (state, action: PayloadAction<string>) => {
            state.loading = true;
            state.error = null;
        },
        fetchListeningContentSuccess: (state, action: PayloadAction<ListeningContent>) => {
            state.content = action.payload;
            state.loading = false;
        },
        fetchListeningContentFailure: (state, action: PayloadAction<string>) => {
            state.loading = false;
            state.error = action.payload;
        },

        // Session Actions
        startListeningSessionRequest: (state, action: PayloadAction<string>) => {
            state.loading = true;
            state.error = null;
        },
        startListeningSessionSuccess: (state, action: PayloadAction<ListeningSession>) => {
            state.session = action.payload;
            state.loading = false;
        },
        startListeningSessionFailure: (state, action: PayloadAction<string>) => {
            state.loading = false;
            state.error = action.payload;
        },

        // Session Creation Actions
        createListeningSessionRequest: (state, action: PayloadAction<{ contentId: string; config?: any }>) => {
            state.loading = true;
            state.error = null;
        },
        createListeningSessionSuccess: (state, action: PayloadAction<ListeningSession>) => {
            state.session = action.payload;
            state.loading = false;
        },
        createListeningSessionFailure: (state, action: PayloadAction<string>) => {
            state.loading = false;
            state.error = action.payload;
        },

        // Session Completion Actions
        completeListeningSessionRequest: (state, action: PayloadAction<{ sessionId: string; answers?: any[] }>) => {
            state.loading = true;
            state.isCompletingSession = true;
            state.error = null;
        },
        completeListeningSessionSuccess: (state, action: PayloadAction<any>) => {
            state.loading = false;
            // Session completion data is handled in the action payload
            // The status is managed by the base_session in the backend
            state.isCompletingSession = false;
        },
        completeListeningSessionFailure: (state, action: PayloadAction<string>) => {
            state.loading = false;
            state.error = action.payload;
            state.isCompletingSession = false;
        },

        // Answer Submission
        submitListeningAnswerRequest: (state, action: PayloadAction<any>) => {
            state.loading = true;
            state.error = null;
        },
        submitListeningAnswerSuccess: (state, action: PayloadAction<any>) => {
            state.loading = false;
            if (state.session) {
                state.session = {
                    ...state.session,
                    ...action.payload,
                };
            }
        },
        submitListeningAnswerFailure: (state, action: PayloadAction<string>) => {
            state.loading = false;
            state.error = action.payload;
        },

        // Audio Upload
        uploadListeningAudioRequest: (state, action: PayloadAction<{ sessionId: string; audioBlob: Blob }>) => {
            state.loading = true;
            state.error = null;
        },
        uploadListeningAudioSuccess: (state, action: PayloadAction<any>) => {
            state.loading = false;
            if (state.session) {
                state.session = {
                    ...state.session,
                    ...action.payload,
                };
            }
        },
        uploadListeningAudioFailure: (state, action: PayloadAction<string>) => {
            state.loading = false;
            state.error = action.payload;
        },

        // Feedback Actions
        fetchListeningFeedbackRequest: (state, action: PayloadAction<string>) => {
            state.loading = true;
            state.error = null;
        },
        fetchListeningFeedbackSuccess: (state, action: PayloadAction<any>) => {
            state.feedback = action.payload;
            state.loading = false;
        },
        fetchListeningFeedbackFailure: (state, action: PayloadAction<string>) => {
            state.loading = false;
            state.error = action.payload;
        },

        // Reset State
        resetListeningState: (state) => {
            return initialState;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchListeningContentsRequest, (state, action) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchListeningContentsSuccess, (state, action) => {
                state.contents = action.payload.results;
                state.totalContents = action.payload.count;
                state.loading = false;
            })
            .addCase(fetchListeningContentsFailure, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

export const {
    fetchListeningContentRequest,
    fetchListeningContentSuccess,
    fetchListeningContentFailure,
    startListeningSessionRequest,
    startListeningSessionSuccess,
    startListeningSessionFailure,
    createListeningSessionRequest,
    createListeningSessionSuccess,
    createListeningSessionFailure,
    completeListeningSessionRequest,
    completeListeningSessionSuccess,
    completeListeningSessionFailure,
    submitListeningAnswerRequest,
    submitListeningAnswerSuccess,
    submitListeningAnswerFailure,
    uploadListeningAudioRequest,
    uploadListeningAudioSuccess,
    uploadListeningAudioFailure,
    fetchListeningFeedbackRequest,
    fetchListeningFeedbackSuccess,
    fetchListeningFeedbackFailure,
    resetListeningState,
    setCurrentListeningContentListPage,
    setListeningContentListFilters,
} = listeningSlice.actions;

export default listeningSlice.reducer;
